import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  CameraIcon,
  PlusIcon,
  Trash2Icon,
  RotateCcwIcon,
  AlertCircleIcon,
  AlertTriangleIcon,
  CheckCircleIcon,
  XIcon,
  Loader2Icon,
  RefreshCwIcon,
} from 'lucide-react'
import { useState, useEffect } from 'react'

import instancesApi, { type VMSnapshot } from '@nebula/shared/services/instancesApi'
import tasksApi, { type TaskStatusResponse } from '@nebula/shared/services/tasksApi'

type SnapshotsTabProps = {
  instanceId: string
}

type SnapshotOpType = 'create' | 'delete' | 'rollback'

type PendingSnapshotOp = {
  type: SnapshotOpType
  snapshotName: string
  taskId: string
}

const OP_LABELS: Record<
  SnapshotOpType,
  { active: string; success: (name: string) => string; errorFallback: (name: string) => string }
> = {
  create: {
    active: 'Criando',
    success: (name) => `Snapshot '${name}' criado com sucesso!`,
    errorFallback: (name) => `Falha ao criar snapshot '${name}'`,
  },
  delete: {
    active: 'Deletando',
    success: (name) => `Snapshot '${name}' deletado com sucesso!`,
    errorFallback: (name) => `Falha ao deletar snapshot '${name}'`,
  },
  rollback: {
    active: 'Restaurando',
    success: (name) => `Rollback para '${name}' concluído com sucesso!`,
    errorFallback: (name) => `Falha ao restaurar snapshot '${name}'`,
  },
}

type TaskTrackerProps = {
  op: PendingSnapshotOp
  onResolve: (op: PendingSnapshotOp, data: TaskStatusResponse) => void
}

function TaskTracker({ op, onResolve }: TaskTrackerProps) {
  const { data } = useQuery({
    queryKey: ['task-status', op.taskId],
    queryFn: () => tasksApi.getStatus(op.taskId),
    refetchInterval: (query) => {
      const state = (query.state.data as TaskStatusResponse | undefined)?.state
      return state === 'SUCCESS' || state === 'FAILURE' ? false : 2000
    },
    refetchIntervalInBackground: true,
  })

  useEffect(() => {
    if (!data) return
    if (data.state === 'SUCCESS' || data.state === 'FAILURE') {
      onResolve(op, data)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.state])

  return null
}

export default function SnapshotsTab({ instanceId }: SnapshotsTabProps) {
  const queryClient = useQueryClient()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState<{
    type: 'delete' | 'rollback'
    snapshot: VMSnapshot
  } | null>(null)
  const [showAlert, setShowAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(
    null,
  )
  const [pendingOps, setPendingOps] = useState<PendingSnapshotOp[]>([])

  const [newSnapshot, setNewSnapshot] = useState({
    snapname: '',
    description: '',
    vmstate: false,
  })

  const handleTaskResolve = (op: PendingSnapshotOp, data: TaskStatusResponse) => {
    setPendingOps((prev) => prev.filter((p) => p.taskId !== op.taskId))
    if (data.state === 'SUCCESS') {
      setShowAlert({ type: 'success', message: OP_LABELS[op.type].success(op.snapshotName) })
      queryClient.invalidateQueries({ queryKey: ['snapshots', instanceId] })
      if (op.type === 'rollback') {
        queryClient.invalidateQueries({ queryKey: ['instance', instanceId] })
      }
    } else {
      const msg = data.error || OP_LABELS[op.type].errorFallback(op.snapshotName)
      setShowAlert({ type: 'error', message: msg })
    }
    setTimeout(() => setShowAlert(null), 5000)
  }

  const isPendingFor = (snapshotName: string) =>
    pendingOps.some((op) => op.snapshotName === snapshotName)

  const { data: snapshots, isLoading } = useQuery({
    queryKey: ['snapshots', instanceId],
    queryFn: () => instancesApi.listSnapshots(instanceId),
    refetchInterval: 10_000, // Refresh every 10s
  })

  const createMutation = useMutation({
    mutationFn: () => instancesApi.createSnapshot(instanceId, newSnapshot),
    onSuccess: (data: any, _vars, _ctx) => {
      const snapshotName = newSnapshot.snapname
      const taskId: string | undefined = data?.task_id
      if (taskId) {
        setPendingOps((prev) => [...prev, { type: 'create', snapshotName, taskId }])
      } else {
        setShowAlert({ type: 'success', message: OP_LABELS.create.success(snapshotName) })
        queryClient.invalidateQueries({ queryKey: ['snapshots', instanceId] })
        setTimeout(() => setShowAlert(null), 5000)
      }
      setShowCreateModal(false)
      setNewSnapshot({ snapname: '', description: '', vmstate: false })
    },
    onError: (error: any) => {
      setShowAlert({
        type: 'error',
        message: error.response?.data?.detail || 'Erro ao criar snapshot',
      })
      setTimeout(() => setShowAlert(null), 5000)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (snapname: string) => instancesApi.deleteSnapshot(instanceId, snapname),
    onSuccess: (data: any, snapname) => {
      const taskId: string | undefined = data?.task_id
      if (taskId) {
        setPendingOps((prev) => [...prev, { type: 'delete', snapshotName: snapname, taskId }])
      } else {
        setShowAlert({ type: 'success', message: OP_LABELS.delete.success(snapname) })
        queryClient.invalidateQueries({ queryKey: ['snapshots', instanceId] })
        setTimeout(() => setShowAlert(null), 5000)
      }
      setShowConfirmModal(null)
    },
    onError: (error: any) => {
      setShowAlert({
        type: 'error',
        message: error.response?.data?.detail || 'Erro ao deletar snapshot',
      })
      setTimeout(() => setShowAlert(null), 5000)
    },
  })

  const refreshUsageMutation = useMutation({
    mutationFn: () => instancesApi.refreshSnapshotsUsage(instanceId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['snapshots', instanceId] })
      const count = data?.snapshots_recorded ?? 0
      setShowAlert({
        type: 'success',
        message:
          count > 0
            ? `Tamanhos atualizados (${count} snapshot${count > 1 ? 's' : ''})`
            : 'Coleta executada — nenhum snapshot retornou medição',
      })
      setTimeout(() => setShowAlert(null), 5000)
    },
    onError: (error: any) => {
      setShowAlert({
        type: 'error',
        message: error.response?.data?.detail || 'Erro ao atualizar tamanhos dos snapshots',
      })
      setTimeout(() => setShowAlert(null), 5000)
    },
  })

  const rollbackMutation = useMutation({
    mutationFn: (snapname: string) => instancesApi.rollbackSnapshot(instanceId, snapname),
    onSuccess: (data: any, snapname) => {
      const taskId: string | undefined = data?.task_id
      if (taskId) {
        setPendingOps((prev) => [...prev, { type: 'rollback', snapshotName: snapname, taskId }])
      } else {
        setShowAlert({ type: 'success', message: OP_LABELS.rollback.success(snapname) })
        queryClient.invalidateQueries({ queryKey: ['snapshots', instanceId] })
        queryClient.invalidateQueries({ queryKey: ['instance', instanceId] })
        setTimeout(() => setShowAlert(null), 5000)
      }
      setShowConfirmModal(null)
    },
    onError: (error: any) => {
      setShowAlert({
        type: 'error',
        message: error.response?.data?.detail || 'Erro ao fazer rollback',
      })
      setTimeout(() => setShowAlert(null), 5000)
    },
  })

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleString('pt-BR')
  }

  const formatGb = (sizeGb?: number) => {
    if (sizeGb === undefined || sizeGb === null) return '—'
    if (sizeGb < 0.01) return '<0,01 GB'
    return `${sizeGb.toFixed(2).replace('.', ',')} GB`
  }

  const formatCurrency = (value?: number, currency = 'BRL') => {
    if (value === undefined || value === null) return '—'
    try {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency,
      }).format(value)
    } catch {
      return `${currency} ${value.toFixed(2)}`
    }
  }

  // Agregados para banner de snapshots antigos (>30 dias)
  const STALE_THRESHOLD_DAYS = 30
  const staleSnapshots = (snapshots || []).filter((s) => (s.age_days ?? 0) > STALE_THRESHOLD_DAYS)
  const staleMonthlyTotal = staleSnapshots.reduce(
    (acc, s) => acc + (s.estimated_monthly_cost || 0),
    0,
  )
  const staleCurrency = staleSnapshots.find((s) => s.currency)?.currency || 'BRL'

  return (
    <div className="space-y-6">
      {/* Task trackers (invisíveis) — fazem polling do status de cada op pendente */}
      {pendingOps.map((op) => (
        <TaskTracker key={op.taskId} op={op} onResolve={handleTaskResolve} />
      ))}

      {/* Banner de operações em andamento */}
      {pendingOps.length > 0 && (
        <div className="space-y-2 rounded-lg border border-blue-200 bg-blue-50 p-4">
          {pendingOps.map((op) => (
            <div key={op.taskId} className="flex items-center space-x-3 text-sm text-blue-900">
              <Loader2Icon className="size-4 shrink-0 animate-spin text-blue-600" />
              <span>
                {OP_LABELS[op.type].active} snapshot <strong>'{op.snapshotName}'</strong>... (em
                andamento)
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Alert */}
      {showAlert && (
        <div
          className={`flex items-start space-x-3 rounded-lg p-4 ${
            showAlert.type === 'success'
              ? 'border border-green-200 bg-green-50'
              : 'border border-red-200 bg-red-50'
          }`}
        >
          {showAlert.type === 'success' ? (
            <CheckCircleIcon className="mt-0.5 size-5 text-green-600" />
          ) : (
            <AlertCircleIcon className="mt-0.5 size-5 text-red-600" />
          )}
          <p
            className={`text-sm font-medium ${
              showAlert.type === 'success' ? 'text-green-800' : 'text-red-800'
            }`}
          >
            {showAlert.message}
          </p>
        </div>
      )}

      {/* Header */}
      <div className="rounded-lg bg-white p-6 shadow">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <CameraIcon className="size-6 text-purple-600" />
            <h2 className="text-lg font-semibold text-gray-900">Snapshots da VM</h2>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => refreshUsageMutation.mutate()}
              disabled={refreshUsageMutation.isPending}
              title="Coleta sob demanda do tamanho e custo dos snapshots"
              className="flex items-center space-x-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {refreshUsageMutation.isPending ? (
                <Loader2Icon className="size-4 animate-spin" />
              ) : (
                <RefreshCwIcon className="size-4" />
              )}
              <span>
                {refreshUsageMutation.isPending ? 'Atualizando...' : 'Atualizar tamanhos'}
              </span>
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center space-x-2 rounded-lg bg-purple-600 px-4 py-2 text-white hover:bg-purple-700"
            >
              <PlusIcon className="size-4" />
              <span>Criar Snapshot</span>
            </button>
          </div>
        </div>

        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-start space-x-3">
            <AlertCircleIcon className="mt-0.5 size-5 text-blue-600" />
            <div className="text-sm text-blue-800">
              <p className="font-medium">Sobre Snapshots</p>
              <p className="mt-1">
                Snapshots permitem salvar o estado atual da VM para restauração posterior. Use-os
                antes de fazer mudanças importantes no sistema.
              </p>
            </div>
          </div>
        </div>

        {staleSnapshots.length > 0 && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4">
            <div className="flex items-start space-x-3">
              <AlertTriangleIcon className="mt-0.5 size-5 text-amber-600" />
              <div className="text-sm text-amber-900">
                <p className="font-medium">
                  {staleSnapshots.length} snapshot{staleSnapshots.length > 1 ? 's' : ''} com mais de{' '}
                  {STALE_THRESHOLD_DAYS} dias
                </p>
                <p className="mt-1">
                  Estes snapshots seguem acumulando custo enquanto existirem. Custo estimado mensal:{' '}
                  <strong>{formatCurrency(staleMonthlyTotal, staleCurrency)}</strong>. Se não
                  precisar mais deles, considere remover para reduzir o gasto.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Snapshots List */}
      <div className="overflow-hidden rounded-lg bg-white shadow">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="mx-auto size-8 animate-spin rounded-full border-b-2 border-purple-600"></div>
            <p className="mt-2 text-gray-600">Carregando snapshots...</p>
          </div>
        ) : snapshots && snapshots.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                    Nome
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                    Descrição
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                    Data/Hora
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium tracking-wider text-gray-500 uppercase">
                    Tamanho
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium tracking-wider text-gray-500 uppercase">
                    Custo/mês
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                    RAM
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium tracking-wider text-gray-500 uppercase">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {snapshots.map((snapshot) => (
                  <tr key={snapshot.name} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <CameraIcon className="mr-2 size-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900">{snapshot.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-600">{snapshot.description || '-'}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600">
                        {snapshot.snaptime ? formatDate(snapshot.snaptime) : '-'}
                      </span>
                      {snapshot.age_days !== undefined && snapshot.age_days !== null && (
                        <p
                          className={`mt-0.5 text-xs ${
                            snapshot.age_days > STALE_THRESHOLD_DAYS
                              ? 'font-medium text-amber-600'
                              : 'text-gray-400'
                          }`}
                        >
                          {snapshot.age_days === 0
                            ? 'hoje'
                            : `há ${snapshot.age_days} dia${snapshot.age_days > 1 ? 's' : ''}`}
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right whitespace-nowrap">
                      <span
                        className="text-sm text-gray-900"
                        title={snapshot.usage_notes || undefined}
                      >
                        {formatGb(snapshot.size_gb)}
                      </span>
                      {snapshot.storage_backend && snapshot.storage_backend !== 'rbd' && (
                        <p className="mt-0.5 text-xs text-amber-600">
                          backend {snapshot.storage_backend} não medido
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {formatCurrency(snapshot.estimated_monthly_cost, snapshot.currency)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`rounded-full px-2 py-1 text-xs font-medium ${
                          snapshot.vmstate
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {snapshot.vmstate ? 'Incluído' : 'Não incluído'}
                      </span>
                    </td>
                    <td className="space-x-2 px-6 py-4 text-right text-sm font-medium whitespace-nowrap">
                      <button
                        onClick={() => setShowConfirmModal({ type: 'rollback', snapshot })}
                        disabled={isPendingFor(snapshot.name)}
                        className="inline-flex items-center space-x-1 text-purple-600 hover:text-purple-900 disabled:cursor-not-allowed disabled:opacity-40 hover:disabled:text-purple-600"
                      >
                        <RotateCcwIcon className="size-4" />
                        <span>Restaurar</span>
                      </button>
                      <button
                        onClick={() => setShowConfirmModal({ type: 'delete', snapshot })}
                        disabled={isPendingFor(snapshot.name)}
                        className="inline-flex items-center space-x-1 text-red-600 hover:text-red-900 disabled:cursor-not-allowed disabled:opacity-40 hover:disabled:text-red-600"
                      >
                        <Trash2Icon className="size-4" />
                        <span>Deletar</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-8 text-center text-gray-500">
            <CameraIcon className="mx-auto mb-3 size-12 text-gray-300" />
            <p>Nenhum snapshot encontrado</p>
            <p className="mt-1 text-sm">Crie seu primeiro snapshot para começar</p>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Criar Snapshot</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XIcon className="size-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Nome do Snapshot *
                </label>
                <input
                  type="text"
                  value={newSnapshot.snapname}
                  onChange={(e) => setNewSnapshot({ ...newSnapshot, snapname: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-purple-600"
                  placeholder="snapshot-backup-01"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Descrição (opcional)
                </label>
                <textarea
                  value={newSnapshot.description}
                  onChange={(e) => setNewSnapshot({ ...newSnapshot, description: e.target.value })}
                  rows={3}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-purple-600"
                  placeholder="Backup antes de atualização..."
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="vmstate"
                  checked={newSnapshot.vmstate}
                  onChange={(e) => setNewSnapshot({ ...newSnapshot, vmstate: e.target.checked })}
                  className="size-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <label htmlFor="vmstate" className="ml-2 block text-sm text-gray-700">
                  Incluir estado da RAM (apenas se VM estiver ligada)
                </label>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowCreateModal(false)}
                disabled={createMutation.isPending}
                className="rounded-lg bg-gray-200 px-4 py-2 text-gray-700 hover:bg-gray-300 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={() => createMutation.mutate()}
                disabled={!newSnapshot.snapname || createMutation.isPending}
                className="rounded-lg bg-purple-600 px-4 py-2 text-white hover:bg-purple-700 disabled:opacity-50"
              >
                {createMutation.isPending ? 'Criando...' : 'Criar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      {showConfirmModal && (
        <div className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6">
            <div className="mb-4 flex items-center space-x-3">
              <div
                className={`rounded-lg p-2 ${
                  showConfirmModal.type === 'delete' ? 'bg-red-100' : 'bg-purple-100'
                }`}
              >
                {showConfirmModal.type === 'delete' ? (
                  <Trash2Icon className="size-6 text-red-600" />
                ) : (
                  <RotateCcwIcon className="size-6 text-purple-600" />
                )}
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {showConfirmModal.type === 'delete' ? 'Deletar Snapshot' : 'Restaurar Snapshot'}
              </h3>
            </div>

            <p className="mb-6 text-sm text-gray-600">
              {showConfirmModal.type === 'delete' ? (
                <>
                  Tem certeza que deseja deletar o snapshot{' '}
                  <strong>{showConfirmModal.snapshot.name}</strong>? Esta ação não pode ser
                  desfeita.
                </>
              ) : (
                <>
                  Tem certeza que deseja restaurar a VM para o snapshot{' '}
                  <strong>{showConfirmModal.snapshot.name}</strong>? O estado atual da VM será
                  substituído.
                </>
              )}
            </p>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowConfirmModal(null)}
                disabled={deleteMutation.isPending || rollbackMutation.isPending}
                className="rounded-lg bg-gray-200 px-4 py-2 text-gray-700 hover:bg-gray-300 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  if (showConfirmModal.type === 'delete') {
                    deleteMutation.mutate(showConfirmModal.snapshot.name)
                  } else {
                    rollbackMutation.mutate(showConfirmModal.snapshot.name)
                  }
                }}
                disabled={deleteMutation.isPending || rollbackMutation.isPending}
                className={`rounded-lg px-4 py-2 text-white disabled:opacity-50 ${
                  showConfirmModal.type === 'delete'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-purple-600 hover:bg-purple-700'
                }`}
              >
                {deleteMutation.isPending || rollbackMutation.isPending
                  ? 'Processando...'
                  : showConfirmModal.type === 'delete'
                    ? 'Deletar'
                    : 'Restaurar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
